import React, { useState, useRef, useEffect } from 'react';
import './TemplateGallery.css';

const TemplateCard = ({ template, onOpenTemplate }) => {
  const { thumbnail, name, theme, _id } = template;
  const title = name || 'Template';
  const subtitle = theme || '';

  const openTemplate = (template) => {
    onOpenTemplate(template);
  };

  return (
    <div className="tg-card" role="button" onClick={() => openTemplate(template)} title={title}>
      <div className="tg-thumbnail">
        {thumbnail ? (
          <img src={thumbnail} alt={title} />
        ) : (
          <div className="tg-placeholder">{title.charAt(0)}</div>
        )}
      </div>
      <div className="tg-card-title">{title}</div>
      {subtitle ? <div className="tg-card-subtitle">{subtitle}</div> : null}
    </div>
  );
};

const TemplateGallery = ({ templates, onCreateBlank, onOpenTemplate }) => {
  const [showAll, setShowAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6); // default
  const previewRef = useRef(null);

  useEffect(() => {
    const calculateVisibleCount = () => {
      if (previewRef.current) {
        const container = previewRef.current;
        const containerWidth = container.offsetWidth;
        const cardMinWidth = 110;
        const gap = 18;
        const numCards = Math.floor((containerWidth + gap) / (cardMinWidth + gap));
        setVisibleCount(Math.max(1, numCards));
      }
    };

    calculateVisibleCount();
    window.addEventListener('resize', calculateVisibleCount);

    // Also recalculate on zoom (which triggers resize)
    const observer = new ResizeObserver(calculateVisibleCount);
    if (previewRef.current) {
      observer.observe(previewRef.current);
    }

    return () => {
      window.removeEventListener('resize', calculateVisibleCount);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="tg-wrapper">
      <div className={`tg-inner ${showAll ? 'tg-inner--expanded' : 'tg-inner--preview'}`}>
        <div className="tg-header">
          <h4 className="tg-title">Start a new document</h4>
          {templates.length > 0 && (
            <div
              className={`tg-controls ${templates.length <= visibleCount ? 'tg-controls--disabled' : ''}`}
              role="button"
              aria-expanded={showAll}
              onClick={templates.length > visibleCount ? () => setShowAll((s) => !s) : undefined}
              tabIndex={templates.length > visibleCount ? 0 : -1}
              onKeyPress={templates.length > visibleCount ? (e) => { if (e.key === 'Enter' || e.key === ' ') setShowAll(s => !s); } : undefined}
            >
              <span className="tg-controls-text">View Template Gallery</span>
              {templates.length > visibleCount && <span className={`tg-controls-caret ${showAll ? 'open' : ''}`}>▾</span>}
            </div>
          )}
        </div>

        {templates.length === 0 ? (
          <div className="tg-no-templates">No templates available.</div>
        ) : (
          <>
            {!showAll && (
              <div ref={previewRef} className={`tg-list tg-list--preview`}>
                {templates.slice(0, visibleCount).map((template) => (
                  <TemplateCard key={template._id} template={template} onOpenTemplate={onOpenTemplate} />
                ))}
              </div>
            )}
            {/* Expanded grid: appears below header as a full-width section */}
            {showAll && (
              <div className="tg-expanded">
                <div className="tg-list tg-list--expanded">
                  {templates.map((template) => (
                    <TemplateCard key={template._id} template={template} onOpenTemplate={onOpenTemplate} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default TemplateGallery;
